import React from 'react';
import { cn } from '@/lib/core/utils';
import { Button } from '@/components/ui/button';
import { LogIn, LogOut, Coffee, Play, Loader2 } from 'lucide-react';
import type { AuthenticatedEmployee } from '@/hooks/kiosk';
import type { ClockAction } from '@/hooks/kiosk';

interface ClockActionButtonsProps {
  employee: AuthenticatedEmployee;
  breakTrackingEnabled?: boolean;
  isProcessing: boolean;
  onAction: (action: ClockAction) => void;
  className?: string;
}

/**
 * Clock action buttons based on employee's current status
 */
export function ClockActionButtons({
  employee,
  breakTrackingEnabled = true,
  isProcessing,
  onAction,
  className,
}: ClockActionButtonsProps) {
  const { current_status } = employee;

  const renderButtons = () => {
    switch (current_status) {
      case 'not_clocked_in':
        return (
          <Button
            onClick={() => onAction('arrival')}
            disabled={isProcessing}
            size="lg"
            className={cn(
              'w-full h-20 text-xl font-semibold',
              'bg-green-600 hover:bg-green-700 text-white',
              'active:scale-[0.98] transition-transform'
            )}
          >
            {isProcessing ? (
              <Loader2 className="w-6 h-6 mr-3 animate-spin" />
            ) : (
              <LogIn className="w-6 h-6 mr-3" />
            )}
            Clock In
          </Button>
        );

      case 'clocked_in':
        return (
          <div className="flex flex-col gap-4">
            {/* Break button (if enabled) */}
            {breakTrackingEnabled && (
              <Button
                onClick={() => onAction('break_start')}
                disabled={isProcessing}
                size="lg"
                variant="outline"
                className={cn(
                  'w-full h-16 text-lg font-semibold',
                  'border-orange-300 text-orange-600 hover:bg-orange-50',
                  'dark:border-orange-500/50 dark:text-orange-400 dark:hover:bg-orange-500/10',
                  'active:scale-[0.98] transition-transform'
                )}
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Coffee className="w-5 h-5 mr-2" />
                )}
                Start Break
              </Button>
            )}

            {/* Clock out button */}
            <Button
              onClick={() => onAction('departure')}
              disabled={isProcessing}
              size="lg"
              variant="destructive"
              className={cn(
                'w-full h-20 text-xl font-semibold',
                'active:scale-[0.98] transition-transform'
              )}
            >
              {isProcessing ? (
                <Loader2 className="w-6 h-6 mr-3 animate-spin" />
              ) : (
                <LogOut className="w-6 h-6 mr-3" />
              )}
              Clock Out
            </Button>
          </div>
        );

      case 'on_break':
        return (
          <div className="flex flex-col gap-4">
            {/* End break button */}
            <Button
              onClick={() => onAction('break_end')}
              disabled={isProcessing}
              size="lg"
              className={cn(
                'w-full h-20 text-xl font-semibold',
                'bg-green-600 hover:bg-green-700 text-white',
                'active:scale-[0.98] transition-transform'
              )}
            >
              {isProcessing ? (
                <Loader2 className="w-6 h-6 mr-3 animate-spin" />
              ) : (
                <Play className="w-6 h-6 mr-3" />
              )}
              End Break
            </Button>

            {/* Clock out without ending break (emergency) */}
            <Button
              onClick={() => onAction('departure')}
              disabled={isProcessing}
              variant="outline"
              size="lg"
              className={cn(
                'w-full h-14 text-base font-medium',
                'border-red-300 text-red-600 hover:bg-red-50',
                'dark:border-red-500/50 dark:text-red-400 dark:hover:bg-red-500/10',
                'active:scale-[0.98] transition-transform'
              )}
            >
              <LogOut className="w-5 h-5 mr-2" />
              End Shift (skip break end)
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={cn('w-full', className)}>
      {renderButtons()}
    </div>
  );
}
